import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import packages, { apiError, errorMessage } from 'common/packages';
import { useAuth } from 'common/auth';
import NotPackageManager, { usePackageManager } from 'components/NotPackageManager';
import {
  AccessPanel,
  AccountPickerDialog,
  AssetPanel,
  DeleteDialog,
  MetadataForm,
  PackageSummary,
  RenameAssetDialog,
} from './components';

// Fields the PATCH form carries, paired with the package property each one
// mirrors. Only changed fields are sent — PATCH is partial.
const FIELDS = [
  { form: 'packageName', property: 'name' },
  { form: 'version', property: 'version' },
  { form: 'type', property: 'type' },
  { form: 'fileName', property: 'file_name' },
  { form: 'label', property: 'label' },
  { form: 'description', property: 'description' },
  { form: 'compatibility', property: 'compatibility' },
  { form: 'manifest', property: 'manifest' },
  { form: 'signature', property: 'signature' },
  { form: 'url', property: 'url' },
  { form: 'isPublic', property: 'is_public', boolean: true },
  { form: 'isVisible', property: 'is_visible', boolean: true },
];

const PackageDetails = () => {
  const { application, uuid } = useParams();
  const navigate = useNavigate();
  const { isManager, error: permissionError } = usePackageManager();
  const { getProfile } = useAuth();
  const profileEmail = (getProfile() || {}).emailAddress || '';

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null);
  const [notice, setNotice] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const [assets, setAssets] = useState({ thumb: null, icon: null });
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [uploading, setUploading] = useState(null);

  const [pendingSave, setPendingSave] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [access, setAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [accessBusy, setAccessBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  // The signed-in account resolved through endpoint K. Only used while the API
  // predates the `caller` object; 'unknown' means the lookup was inconclusive.
  const [selfAccount, setSelfAccount] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return packages
      .get(application, uuid)
      .then((data) => {
        setPkg(data);
        setFatal(null);
      })
      .catch((e) => {
        const failure = apiError(e);
        setPkg(null);
        setFatal(failure);
      })
      .finally(() => setLoading(false));
  }, [application, uuid]);

  useEffect(() => {
    if (isManager !== true) return;
    load();
  }, [isManager, load]);

  // Presigned GET URLs live 5 minutes, so they are fetched on demand and
  // refreshed in place — a detail page left open keeps rendering its images.
  useEffect(() => {
    if (!pkg || !pkg.__links) return undefined;
    let active = true;
    const timers = [];

    const fetchUri = (key, link) => {
      if (!link) {
        setAssets((previous) => ({ ...previous, [key]: null }));
        return;
      }
      packages
        .assetUri(link)
        .then(({ uri, ttl }) => {
          if (!active) return;
          setAssets((previous) => ({ ...previous, [key]: uri }));
          const delay = Math.max(((ttl || 300) - 30) * 1000, 30000);
          timers.push(setTimeout(() => fetchUri(key, link), delay));
        })
        .catch(() => {
          if (active) setAssets((previous) => ({ ...previous, [key]: null }));
        });
    };

    fetchUri('thumb', pkg.__links.thumb);
    fetchUri('icon', pkg.__links.icon);

    return () => {
      active = false;
      timers.forEach(clearTimeout);
    };
  }, [pkg]);

  const isPublic = Boolean(pkg && pkg.is_public);
  // __links.access is the capability flag: present only when the caller may
  // manage this package's list, so it gates the grant and revoke actions. The
  // read is still attempted without it — the endpoint is manager-policy gated and
  // answers for itself, and while it is undeployed no package carries the link at
  // all, which would otherwise render every restricted package as unmanageable
  // rather than as waiting on the API.
  const canManageAccess = Boolean(pkg && pkg.__links && pkg.__links.access);

  const loadAccess = useCallback(() => {
    setAccessLoading(true);
    return packages
      .accessList(application, uuid)
      .then((data) => {
        setAccess(data);
        setAccessError(null);
      })
      .catch((e) => {
        setAccess(null);
        setAccessError(apiError(e));
      })
      .finally(() => setAccessLoading(false));
  }, [application, uuid]);

  // A public package evaluates no access at all, so nothing is fetched for one —
  // flipping is_public back re-runs this and the same grants reappear untouched.
  useEffect(() => {
    if (isPublic) {
      setAccess(null);
      setAccessError(null);
      return;
    }
    loadAccess();
  }, [isPublic, loadAccess]);

  // Endpoint I is keyed by account UUID, which nothing on this page carries for
  // the signed-in user. Matched on an exact email, and only ever used when it
  // resolves to exactly one account — a near miss opens the picker instead.
  const findSelfAccount = useCallback(() => {
    // 'unknown' is cached too: retrying would repeat the same inconclusive query,
    // and the recovery is the picker, not another lookup.
    if (selfAccount) return Promise.resolve(selfAccount);
    if (!profileEmail) {
      setSelfAccount('unknown');
      return Promise.resolve('unknown');
    }
    return packages
      .candidateAccounts(application, { q: profileEmail, page: 1, page_size: 25 })
      .then((data) => {
        const matches = ((data && data.accounts) || []).filter(
          (a) => a.email && a.email.toLowerCase() === profileEmail.toLowerCase()
        );
        const resolved = matches.length === 1 ? matches[0] : 'unknown';
        setSelfAccount(resolved);
        return resolved;
      })
      .catch(() => {
        setSelfAccount('unknown');
        return 'unknown';
      });
  }, [application, profileEmail, selfAccount]);

  // Fallback only. Once the API returns `caller` this never runs.
  useEffect(() => {
    if (isPublic || !access || access.caller) return;
    findSelfAccount();
  }, [isPublic, access, findSelfAccount]);

  // Whether the signed-in account may download this package, and if not, which
  // half is missing — they are different fixes and only the grant is self-serviceable.
  const selfPosition = useMemo(() => {
    if (isPublic) return null;
    // No list and no caller object means neither source answered. Say so rather
    // than asserting a state.
    if (!access) return accessError ? { known: false } : null;
    if (access.caller) {
      return {
        known: true,
        granted: access.caller.is_granted === true,
        licensed: access.caller.is_licensed === true,
      };
    }
    if (selfAccount && selfAccount !== 'unknown') {
      const rows = access.access || [];
      return {
        known: true,
        granted: rows.some((row) => row.account_uuid === selfAccount.uuid),
        licensed: selfAccount.is_licensed === true,
      };
    }
    if (selfAccount === 'unknown') return { known: false };
    return null;
  }, [isPublic, access, accessError, selfAccount]);

  const grantedUuids = useMemo(
    () => ((access && access.access) || []).map((row) => row.account_uuid),
    [access]
  );

  const fail = (e) => {
    const failure = apiError(e);
    setNotice({ severity: 'error', message: errorMessage(failure) });
    if (failure.code === 'invalid_compatibility') {
      setFieldErrors({ compatibility: errorMessage(failure) });
    }
    if (failure.code === 'invalid_manifest') {
      setFieldErrors({ manifest: errorMessage(failure) });
    }
    return failure;
  };

  const runLinkAction = (link, action, done) => {
    setBusy(true);
    setNotice(null);
    action(link)
      .then(() => load())
      .then(() => setNotice({ severity: 'success', message: done }))
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const handleDownload = (link) => {
    packages
      .assetUri(link)
      .then(({ uri }) => window.open(uri, '_blank', 'noopener,noreferrer'))
      .catch(fail);
  };

  const handleVerify = () => {
    setVerifying(true);
    setNotice(null);
    packages
      .verify(pkg.__links.verify)
      .then(setVerification)
      .catch(fail)
      .finally(() => setVerifying(false));
  };

  const submitPatch = (values, strategy, file) => {
    const form = new FormData();
    FIELDS.forEach(({ form: field, property, boolean }) => {
      const current = boolean ? Boolean(pkg[property]) : pkg[property] || '';
      const next = boolean ? Boolean(values[field]) : values[field] || '';
      if (current !== next) form.append(field, boolean ? String(next) : next);
    });
    if (strategy) form.append('assetStrategy', strategy);

    setBusy(true);
    setNotice(null);
    setFieldErrors({});

    return packages
      .patch(application, pkg.uuid, form)
      .then(async (response) => {
        // The metadata is already saved at this point, so a failed PUT must not
        // be reported as if nothing happened.
        let uploadFailure = null;
        if (strategy === 'reupload' && file && response.uploadUrls) {
          try {
            await packages.putToS3(response.uploadUrls.file, file, {
              contentType: 'application/octet-stream',
              onProgress: (progress) => setUploading({ key: 'file', progress }),
            });
          } catch (e) {
            uploadFailure = apiError(e);
          } finally {
            setUploading(null);
          }
        }
        setPkg(response.package);
        setEditing(false);
        setVerification(null);

        const orphaned = response.assets && response.assets.orphaned;
        if (uploadFailure) {
          setNotice({
            severity: 'warning',
            message: `Metadata saved, but the new artifact did not upload (${uploadFailure.message}). The package now points at a file that is not there — Verify, then Re-upload artifact.`,
          });
        } else if (orphaned && orphaned.length > 0) {
          setNotice({
            severity: 'warning',
            message: `Saved. These stored objects are now orphaned and were left in place: ${orphaned.join(', ')}`,
          });
        } else {
          setNotice({ severity: 'success', message: 'Saved.' });
        }
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };

  // Renaming the file or changing the type moves where the artifact is expected
  // to live, so the strategy is settled before anything is written.
  const handleSave = (values) => {
    const renaming =
      (values.fileName || '') !== (pkg.file_name || '') ||
      (values.type || '') !== (pkg.type || '');
    if (renaming) {
      setPendingSave(values);
      return;
    }
    submitPatch(values, null, null);
  };

  const handleReupload = (key, file) => {
    setUploading({ key, progress: 0 });
    setNotice(null);
    packages
      .uploadUrls(pkg.__links.upload_urls)
      .then(({ uploadUrls }) =>
        packages.putToS3(uploadUrls[key], file, {
          // The artifact URL is signed for the register default; thumbnail and
          // icon URLs are signed for image/png.
          contentType: key === 'file' ? 'application/octet-stream' : 'image/png',
          onProgress: (progress) => setUploading({ key, progress }),
        })
      )
      .then(() => load())
      .then(() => {
        setVerification(null);
        setNotice({ severity: 'success', message: 'Upload finished. Verify to confirm.' });
      })
      .catch(fail)
      .finally(() => setUploading(null));
  };

  const handleGrant = (account) => {
    setAccessBusy(true);
    setNotice(null);
    packages
      .grantAccess(application, pkg.uuid, account.uuid)
      .then(() => loadAccess())
      .then(() => {
        setPickerOpen(false);
        setNotice({
          severity: 'success',
          message: `${account.email || account.name || 'That account'} can now download this package.`,
        });
      })
      // The picker stays open on failure, so duplicate_grant lands next to the
      // row that caused it.
      .catch(fail)
      .finally(() => setAccessBusy(false));
  };

  const handleRevoke = (row) => {
    setAccessBusy(true);
    setNotice(null);
    packages
      .revokeAccess(application, pkg.uuid, row.account_uuid)
      .then(() => loadAccess())
      .then(() =>
        setNotice({
          severity: 'success',
          message: `${row.account_email || row.account_name || 'That account'} can no longer download this package.`,
        })
      )
      .catch((e) => {
        fail(e);
        // A revoke that hit grant_not_found means the list on screen is stale.
        return loadAccess();
      })
      .finally(() => setAccessBusy(false));
  };

  const handleSelfGrant = () => {
    setAccessBusy(true);
    setNotice(null);
    findSelfAccount()
      .then((account) => {
        if (account === 'unknown') {
          setPickerQuery(profileEmail);
          setPickerOpen(true);
          setNotice({
            severity: 'warning',
            message:
              'Could not match the signed-in profile to exactly one account — pick the account to grant from the list.',
          });
          return null;
        }
        return packages
          .grantAccess(application, pkg.uuid, account.uuid)
          .then(() => loadAccess())
          .then(() =>
            setNotice({
              severity: 'success',
              message: `Granted access to ${account.email || account.uuid}. It can download this package as soon as it also holds a licence for ${application}.`,
            })
          );
      })
      .catch(fail)
      .finally(() => setAccessBusy(false));
  };

  const handleDelete = () => {
    setBusy(true);
    packages
      .remove(application, pkg.uuid)
      .then(() => navigate(`/packages/${application}`, { replace: true }))
      .catch((e) => {
        fail(e);
        setDeleteOpen(false);
      })
      .finally(() => setBusy(false));
  };

  if (permissionError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Could not check package-management permissions: {permissionError.message}
        </Alert>
      </Box>
    );
  }

  if (isManager === null) {
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Before the loading check below: a non-manager never triggers a fetch, so
  // loading would stay true and hide this block forever.
  if (isManager === false) {
    return (
      <Box sx={{ p: 4 }}>
        <NotPackageManager />
      </Box>
    );
  }

  if (loading && !pkg) {
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!pkg) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert
          severity={fatal && fatal.status === 404 ? 'warning' : 'error'}
          action={
            <Button component={RouterLink} to={`/packages/${application}`} size="small">
              Back to the list
            </Button>
          }
        >
          <AlertTitle>
            {fatal && fatal.status === 404
              ? 'That package does not exist'
              : 'Could not load the package'}
          </AlertTitle>
          {fatal ? errorMessage(fatal) : null}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Stack spacing={3}>
        {notice && (
          <Alert severity={notice.severity} onClose={() => setNotice(null)}>
            {notice.message}
          </Alert>
        )}

        <PackageSummary
          application={application}
          pkg={pkg}
          thumbUri={assets.thumb}
          iconUri={assets.icon}
          busy={busy}
          editing={editing}
          onEdit={() => setEditing((previous) => !previous)}
          onPublish={() =>
            runLinkAction(pkg.__links.publish, packages.publish, 'Published. It is live now.')
          }
          onUnpublish={() =>
            runLinkAction(
              pkg.__links.unpublish,
              packages.unpublish,
              'Unpublished. Clients can no longer resolve it.'
            )
          }
          onVerify={handleVerify}
          onDownload={() => handleDownload(pkg.__links.download)}
          onDelete={() => setDeleteOpen(true)}
          selfPosition={selfPosition}
          selfEmail={profileEmail}
          selfGranting={accessBusy}
          onSelfGrant={handleSelfGrant}
        />

        <MetadataForm
          pkg={pkg}
          readOnly={!editing}
          saving={busy}
          fieldErrors={fieldErrors}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
        />

        {/* Hidden outright on a public package: decision 1 means nothing is
            evaluated there, so a list would describe a check that never runs. */}
        {!pkg.is_public && (
          <AccessPanel
            application={application}
            pkg={pkg}
            rows={(access && access.access) || []}
            loading={accessLoading}
            error={accessError}
            busy={accessBusy}
            canManage={canManageAccess}
            onAdd={() => {
              setPickerQuery('');
              setPickerOpen(true);
            }}
            onRevoke={handleRevoke}
          />
        )}

        <AssetPanel
          verification={verification}
          verifying={verifying}
          uploading={uploading}
          canUpload={Boolean(pkg.__links && pkg.__links.upload_urls)}
          onVerify={pkg.__links && pkg.__links.verify ? handleVerify : null}
          onReupload={handleReupload}
        />
      </Stack>

      <RenameAssetDialog
        open={Boolean(pendingSave)}
        pkg={pkg}
        values={pendingSave}
        saving={busy}
        onCancel={() => setPendingSave(null)}
        onConfirm={(strategy, file) => {
          const values = pendingSave;
          setPendingSave(null);
          submitPatch(values, strategy, file);
        }}
      />

      <AccountPickerDialog
        open={pickerOpen}
        application={application}
        initialQuery={pickerQuery}
        grantedUuids={grantedUuids}
        busy={accessBusy}
        onGrant={handleGrant}
        onClose={() => setPickerOpen(false)}
      />

      <DeleteDialog
        open={deleteOpen}
        pkg={pkg}
        deleting={busy}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

export default PackageDetails;
